export enum ShopifyStore {
  IN = 'IN',
  AE = 'AE',
  US = 'US',
  EU = 'EU',
  GB = 'GB',
  ROW = 'ROW',
  AU = 'AU',
  CA = 'CA',
  SA = 'SA',
  MX = 'MX',
  ZA = 'ZA',
  JP = 'JP',
}

/**
 * Store groups that share the same Shopify backend infrastructure.
 * Stores within a group use the same variant IDs and checkout URLs,
 * but have different prices and currencies.
 */
export const StoreAliasGroups = {
  EU_UK_AU: [
    ShopifyStore.EU,
    ShopifyStore.GB,
    ShopifyStore.AU,
    ShopifyStore.SA,
    ShopifyStore.MX,
    ShopifyStore.ZA,
    ShopifyStore.JP,
  ],
  US_CA: [ShopifyStore.US, ShopifyStore.CA],
} as const;

/**
 * Maps each store to its base store (the one with actual Shopify product configurations).
 * Aliased stores inherit variant IDs and checkout URLs from their base store.
 *
 * @param store - The store to get the base for
 * @returns The base store that contains the actual product configuration
 */
export const getBaseStore = (store: ShopifyStore): ShopifyStore => {
  // GB, AU, SA, MX, ZA, and JP use EU's product configuration
  if (
    [
      ShopifyStore.GB,
      ShopifyStore.AU,
      ShopifyStore.SA,
      ShopifyStore.MX,
      ShopifyStore.ZA,
      ShopifyStore.JP,
    ].includes(store)
  ) {
    return ShopifyStore.EU;
  }
  // CA uses US's product configuration
  if (store === ShopifyStore.CA) {
    return ShopifyStore.US;
  }
  // All other stores are their own base
  return store;
};

/**
 * Maps a store to its effective store for affiliate discount configuration lookup.
 * Uses the baseStore configuration to determine the discount store mapping.
 *
 * @param store - The original ShopifyStore
 * @returns The base store to use for discount config lookup (e.g., AU → EU, CA → US)
 */
export const getEffectiveStoreForDiscount = (
  store: ShopifyStore | null | undefined,
): ShopifyStore => {
  if (!store) return ShopifyStore.ROW;

  // Use the baseStore mapping from getBaseStore
  return getBaseStore(store);
};

export const shopifyCountryMapper = {
  [ShopifyStore.IN]: 'in',
  [ShopifyStore.AE]: 'ae',
  [ShopifyStore.US]: 'us',
  [ShopifyStore.EU]: 'eu',
  [ShopifyStore.GB]: 'gb',
  [ShopifyStore.ROW]: 'global',
  [ShopifyStore.AU]: 'au',
  [ShopifyStore.CA]: 'ca',
  [ShopifyStore.SA]: 'sa',
  [ShopifyStore.MX]: 'mx',
  [ShopifyStore.ZA]: 'za',
  [ShopifyStore.JP]: 'jp',
};

// SHOPIFY_area_TOKEN => PROD
// NEXT_PUBLIC_SHOPIFY_area_TOKEN => DEV/STAGE

const STOREFRONT_API_PATH = '/api/2023-01/graphql.json';
const TESTING_STOREFRONT_ORIGIN = 'https://uhtesting.myshopify.com';
const IN_ORIGIN = 'https://ultrahumanstore.myshopify.com';
const AE_ORIGIN = 'https://ultrahuman.shop';
const STELCORE_ORIGIN = 'https://stelcore-x-ultrahuman.myshopify.com';
const ROW_ORIGIN = 'https://ultrahuman-us.myshopify.com';
const EU_UK_ORIGIN = 'https://ultrahuman-eu-uk.myshopify.com';

/** Production storefront origin for each logical store. */
export const SHOPIFY_SHOP_ORIGINS: Record<ShopifyStore, string> = {
  [ShopifyStore.IN]: IN_ORIGIN,
  [ShopifyStore.AE]: AE_ORIGIN,
  [ShopifyStore.US]: STELCORE_ORIGIN,
  [ShopifyStore.CA]: STELCORE_ORIGIN,
  [ShopifyStore.ROW]: ROW_ORIGIN,
  [ShopifyStore.EU]: EU_UK_ORIGIN,
  [ShopifyStore.GB]: EU_UK_ORIGIN,
  [ShopifyStore.AU]: EU_UK_ORIGIN,
  [ShopifyStore.SA]: EU_UK_ORIGIN,
  [ShopifyStore.MX]: EU_UK_ORIGIN,
  [ShopifyStore.ZA]: EU_UK_ORIGIN,
  [ShopifyStore.JP]: EU_UK_ORIGIN,
};

function storefrontUrl(store: ShopifyStore): string {
  const origin =
    process.env.NEXT_PUBLIC_ENVIRONMENT === 'PRODUCTION'
      ? SHOPIFY_SHOP_ORIGINS[store]
      : TESTING_STOREFRONT_ORIGIN;
  return `${origin}${STOREFRONT_API_PATH}`;
}

type ShopifyStoreMapType = {
  [store in ShopifyStore]?: { url: string; token: string };
};
export const ShopifyStoreMap: ShopifyStoreMapType = {
  [ShopifyStore.IN]: {
    url: storefrontUrl(ShopifyStore.IN),
    token:
      process.env.SHOPIFY_IN_TOKEN ??
      process.env.NEXT_PUBLIC_SHOPIFY_IN_TOKEN ??
      '',
  },
  [ShopifyStore.AE]: {
    url: storefrontUrl(ShopifyStore.AE),
    token:
      process.env.SHOPIFY_AE_TOKEN ??
      process.env.NEXT_PUBLIC_SHOPIFY_AE_TOKEN ??
      '',
  },
  [ShopifyStore.US]: {
    url: storefrontUrl(ShopifyStore.US),
    //change it later to ROW
    token:
      process.env.SHOPIFY_US_TOKEN ??
      process.env.NEXT_PUBLIC_SHOPIFY_US_TOKEN ??
      '',
  },
  [ShopifyStore.ROW]: {
    url: storefrontUrl(ShopifyStore.ROW),
    //change it later to ROW
    token:
      process.env.SHOPIFY_ROW_TOKEN ??
      process.env.NEXT_PUBLIC_SHOPIFY_ROW_TOKEN ??
      '',
  },
  [ShopifyStore.EU]: {
    url: storefrontUrl(ShopifyStore.EU),
    token:
      process.env.SHOPIFY_EU_TOKEN ??
      process.env.NEXT_PUBLIC_SHOPIFY_EU_TOKEN ??
      '',
  },
  [ShopifyStore.GB]: {
    url: storefrontUrl(ShopifyStore.GB),
    token:
      process.env.SHOPIFY_EU_TOKEN ??
      process.env.NEXT_PUBLIC_SHOPIFY_EU_TOKEN ??
      '',
  },
  [ShopifyStore.AU]: {
    url: storefrontUrl(ShopifyStore.AU),
    token:
      process.env.SHOPIFY_EU_TOKEN ??
      process.env.NEXT_PUBLIC_SHOPIFY_EU_TOKEN ??
      '',
  },
  [ShopifyStore.CA]: {
    url: storefrontUrl(ShopifyStore.CA),
    token:
      process.env.SHOPIFY_US_TOKEN ??
      process.env.NEXT_PUBLIC_SHOPIFY_US_TOKEN ??
      '',
  },
  [ShopifyStore.SA]: {
    url: storefrontUrl(ShopifyStore.SA),
    token:
      process.env.SHOPIFY_EU_TOKEN ??
      process.env.NEXT_PUBLIC_SHOPIFY_EU_TOKEN ??
      '',
  },
  [ShopifyStore.MX]: {
    url: storefrontUrl(ShopifyStore.MX),
    token:
      process.env.SHOPIFY_EU_TOKEN ??
      process.env.NEXT_PUBLIC_SHOPIFY_EU_TOKEN ??
      '',
  },
  [ShopifyStore.ZA]: {
    url: storefrontUrl(ShopifyStore.ZA),
    token:
      process.env.SHOPIFY_EU_TOKEN ??
      process.env.NEXT_PUBLIC_SHOPIFY_EU_TOKEN ??
      '',
  },
  [ShopifyStore.JP]: {
    url: storefrontUrl(ShopifyStore.JP),
    token:
      process.env.SHOPIFY_EU_TOKEN ??
      process.env.NEXT_PUBLIC_SHOPIFY_EU_TOKEN ??
      '',
  },
};

export const postToShopify = async ({
  store,
  query,
  variables = {},
  apiVersion,
}: {
  store: ShopifyStore;
  query: string;
  variables: Record<string, unknown>;
  // Overrides the Storefront API version baked into the store URL (default
  // 2023-01). The Apple Pay routes pass 2024-10 for the cart wallet-payment
  // mutations without moving the rest of the site off the proven version.
  apiVersion?: string;
}) => {
  if (!ShopifyStoreMap[store]?.url || !ShopifyStoreMap[store]?.token)
    return 'Store Not Configured Properly';

  const baseUrl = ShopifyStoreMap[store]?.url ?? '';
  const url = apiVersion
    ? baseUrl.replace(/\/api\/[^/]+\//, `/api/${apiVersion}/`)
    : baseUrl;

  try {
    const result = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token':
          ShopifyStoreMap[store]?.token ?? '',
      },
      body: JSON.stringify({ query, variables }),
    }).then((res) => res.json());

    if (result.errors) {
      console.log('Received Shopify error ❌');
      console.log(JSON.stringify(result));
      return result.errors;
    } else if (!result || !result.data) {
      console.log({ result });
      return 'No results found.';
    }
    return result.data;
  } catch (error) {
    console.log(error);
    return error;
  }
};
